import { Link2, Settings, Unlink, UserPlus } from "lucide-react";

import { toast } from "../../components/ui/sonner";
import { cn } from "../../lib/utils";
import { getIcon } from "../../utils/getIcon";
import {
  selectsCrawlerPreferences,
  supportsMultipleAccounts,
} from "../constants";
import { useDelinkIntegration } from "../hooks/useDelinkIntegration";
import { useFetchConnectedIntegrationAccounts } from "../hooks/useFetchConnectedIntegration";
import { getIntegrationLimitationNote } from "../utils";

import { ErrorState } from "./shared/ErrorState";
import { integrationButton } from "./shared/integrationButton";
import { LoadingState } from "./shared/LoadingState";

import type { ConnectedIntegrationDetail } from "../types/integrations";
import type { ModalStepProps } from "../types/modal";

const formatDate = (timestamp: number) =>
  new Date(timestamp * 1000).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });

export function PolishedManageStep({
  integration,
  integrationScope,
  onClose,
  onConnect,
  onManage,
  isLoading,
}: ModalStepProps) {
  const {
    data: accounts = [],
    isLoading: isLoadingAccounts,
    error,
  } = useFetchConnectedIntegrationAccounts(integration.label, {
    integrationScope,
  });

  const { mutate: delink, isPending: isDelinking } = useDelinkIntegration({
    onSuccess: () => {
      toast.success("Integration delinked successfully");
      onClose?.();
    },
  });

  const note = getIntegrationLimitationNote(integration.label);
  const isBusy = isLoading || isDelinking;
  const canPickFolders = selectsCrawlerPreferences(
    integration.label,
    integrationScope,
  );

  const handleDelink = (account: ConnectedIntegrationDetail) =>
    delink({
      verifiableId: account.verifiable_id,
      source: account.source,
      integrationScope,
    });

  return (
    <div className="flex min-h-0 flex-col">
      <div className="flex items-center gap-4">
        <span className="grid h-11 w-11 shrink-0 place-items-center">
          {getIcon(integration.label, 40)}
        </span>
        <div className="min-w-0">
          <p className="truncate text-lg font-semibold text-foreground">
            {integration.value}
          </p>
          <p className="text-sm text-muted-foreground">
            {accounts.length} {accounts.length === 1 ? "account" : "accounts"}
          </p>
        </div>
      </div>

      <p className="my-4 text-sm leading-relaxed text-muted-foreground">
        Delinking stops an account syncing anything new.
      </p>

      {note && (
        <p className="mb-4 rounded-[10px] bg-primary/[0.06] px-3.5 py-3 text-[13px] leading-5 text-foreground/80">
          <span className="font-semibold text-foreground">Note:</span> {note}
        </p>
      )}

      <h3 className="mb-2 ml-0.5 text-xs font-semibold text-muted-foreground">
        Connected accounts
      </h3>

      {isLoadingAccounts ? (
        <div className="rounded-xl bg-muted/60 p-5">
          <LoadingState message="Loading connection details..." />
        </div>
      ) : error || accounts.length === 0 ? (
        <ErrorState
          title={
            error
              ? "Unable to load connection details"
              : "No connected accounts found"
          }
          variant="error"
        />
      ) : (
        <div className="max-h-[44vh] overflow-y-auto rounded-xl bg-muted/50 shadow-[inset_0_0_0_0.5px_hsl(var(--foreground)/0.1)]">
          {accounts.map((account, index) => (
            <div
              key={`${account.verifiable_id}-${index}`}
              className="flex min-h-[68px] items-center gap-3 border-t border-foreground/[0.08] py-3 pl-4 pr-3 first:border-t-0"
            >
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-foreground">
                  {account.verifiable_id}
                </p>
                <p
                  className={cn(
                    "flex items-center gap-1.5 truncate text-xs",
                    account.is_valid_token
                      ? "text-muted-foreground"
                      : "text-red-600 dark:text-red-400",
                  )}
                >
                  <span
                    aria-hidden
                    className={cn(
                      "h-1.5 w-1.5 shrink-0 rounded-full",
                      account.is_valid_token ? "bg-primary" : "bg-red-500",
                    )}
                  />
                  {account.is_valid_token
                    ? `Connected since ${formatDate(account.created_at)}`
                    : "Access expired. Reconnect to resume."}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {canPickFolders && account.is_valid_token && (
                  <button
                    type="button"
                    className={integrationButton("tonal")}
                    onClick={() => onManage?.(account.verifiable_id)}
                    disabled={isBusy}
                    data-testid="account-manage-button"
                  >
                    <Settings />
                    Manage
                  </button>
                )}
                {!account.is_valid_token && (
                  <button
                    type="button"
                    className={integrationButton("primary")}
                    onClick={() => onConnect?.(account.verifiable_id)}
                    disabled={isBusy}
                    data-testid="account-reconnect-button"
                  >
                    <Link2 />
                    Reconnect
                  </button>
                )}
                <button
                  type="button"
                  className={integrationButton("danger")}
                  onClick={() => handleDelink(account)}
                  disabled={isBusy}
                  data-testid="account-delink-button"
                >
                  <Unlink />
                  Delink
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {supportsMultipleAccounts(integration.label) && (
        <button
          type="button"
          className="mt-3 flex h-11 w-full items-center justify-center gap-2 rounded-xl border-[1.5px] border-dashed border-primary/35 text-sm font-medium text-primary transition-colors hover:border-primary/55 hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-50"
          onClick={() => onConnect?.()}
          disabled={isBusy}
          data-testid="add-new-account-button"
        >
          <UserPlus className="h-4 w-4" />
          Add Another Account
        </button>
      )}

      <div className="mt-7 flex justify-end">
        <button
          type="button"
          className={integrationButton("primary")}
          onClick={onClose}
          disabled={isBusy}
          data-testid="manage-step-close-button"
        >
          Done
        </button>
      </div>
    </div>
  );
}
